import { motion } from "framer-motion";
import { Trophy, Medal } from "lucide-react";

const PRIZES = [
  {
    id: "overall",
    tag: "1st Place",
    title: "Grand Champion",
    amount: "₹20,000",
    subtitle: "The all-round winner. Highest weighted score across every rubric dimension.",
    icon: Trophy,
    size: "large",
    accent: "#2FCC7C",
  },
  {
    id: "runnerup",
    tag: "Runner Up",
    title: "2nd Place",
    amount: "₹10,000",
    subtitle: "Hot on the champion's heels. Second-highest overall score.",
    icon: Medal,
    size: "medium",
    accent: "#2D7BF0",
  },
];

function PrizeCard({ prize, index }) {
  const Icon = prize.icon;
  const isLarge = prize.size === "large";
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.8, delay: index * 0.15, ease: [0.22, 1, 0.36, 1] }}
      className="holo-border min-h-[300px] md:min-h-[340px] hover:scale-[1.015] transition-transform duration-500"
      data-testid={`prize-bento-${prize.id}`}
    >
      <div className="holo-inner relative p-7 md:p-9 h-full flex flex-col justify-between overflow-hidden scanlines">
        {/* soft accent glow */}
        <div
          className="absolute -top-24 -right-24 w-64 h-64 rounded-full blur-3xl opacity-30"
          style={{ background: prize.accent }}
        />
        <div
          className="absolute -bottom-32 -left-16 w-72 h-72 rounded-full blur-3xl opacity-15"
          style={{ background: prize.accent }}
        />

        <div className="relative z-10 flex items-start justify-between">
          <div
            className={`rounded-xl border flex items-center justify-center ${
              isLarge ? "w-14 h-14" : "w-12 h-12"
            }`}
            style={{
              borderColor: `${prize.accent}80`,
              background: `${prize.accent}10`,
            }}
          >
            <Icon
              className={isLarge ? "w-6 h-6" : "w-5 h-5"}
              style={{ color: prize.accent }}
              strokeWidth={2}
            />
          </div>
          <div
            className="font-mono text-[10px] uppercase tracking-[0.3em]"
            style={{ color: prize.accent }}
          >
            {prize.tag}
          </div>
        </div>

        <div className="relative z-10 mt-8">
          {/* Cash amount - the hero of the card */}
          <div
            className={`font-heading font-black tracking-tighter leading-none text-gradient-neon ${
              isLarge ? "text-6xl md:text-7xl lg:text-8xl" : "text-5xl md:text-6xl"
            }`}
          >
            {prize.amount}
          </div>
          <h3 className="mt-4 font-heading font-bold tracking-tight text-xl md:text-2xl text-white">
            {prize.title}
          </h3>
          <p className="mt-3 text-sm md:text-base text-zinc-400 leading-relaxed max-w-md">
            {prize.subtitle}
          </p>
        </div>
      </div>
    </motion.div>
  );
}

export default function Prizes() {
  return (
    <section
      id="prizes"
      className="relative py-16 lg:py-20 px-6 md:px-12 lg:px-24"
      data-testid="prizes-section"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl mb-16"
        >
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#2FCC7C] mb-4">
            // 05 · The spoils
          </div>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter leading-tight">
            Prize <span className="text-gradient-neon">pool</span>.
          </h2>
          <p className="mt-6 text-lg text-zinc-400">
            Two payouts. Every entry is a win, but the sharpest skills take home the cash.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="prizes-bento">
          {PRIZES.map((p, i) => (
            <PrizeCard key={p.id} prize={p} index={i} />
          ))}
        </div>

        {/* Final CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.8 }}
          className="mt-16 text-center"
        >
          <h3 className="font-heading text-3xl md:text-5xl lg:text-6xl font-black tracking-tighter">
            Ready to <span className="text-gradient-neon">ship a skill</span>?
          </h3>
          <p className="mt-4 text-zinc-400 max-w-xl mx-auto">
            One week. Real problems. Real people using what you build.
          </p>
          <div className="mt-8 inline-flex">
            <a
              href="https://forms.gle/6mgpbxdarhwd52Uo7"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="final-cta-btn"
              className="group inline-flex items-center gap-3 px-8 py-4 rounded-full bg-[#2FCC7C] text-black font-bold text-base tracking-tight hover:shadow-[0_0_50px_rgba(47,204,124,0.7)] transition-all duration-300 hover:-translate-y-0.5"
            >
              Register now
              <span className="font-mono text-xs text-black/70">→ Tuesday</span>
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
